/* prettier-ignore */

import { computed, effect, inject, Injectable, NgZone, signal } from '@angular/core';
import { webSocket, WebSocketSubject } from "rxjs/webSocket";
import {
  bufferTime,
  combineLatest,
  concat,
  concatMap,
  EMPTY,
  filter,
  firstValueFrom,
  from,
  groupBy,
  GroupedObservable,
  ignoreElements,
  map,
  merge,
  mergeMap,
  Observable,
  of,
  retryWhen,
  scan,
  share,
  shareReplay,
  skip,
  startWith,
  Subject,
  take,
  takeUntil,
  timer,
  Subscription,
  throwError,
} from "rxjs";
import {
  tap,
  finalize,
  timeout as rxTimeout,
  mapTo,
  catchError,
  delay,
  withLatestFrom,
} from "rxjs/operators";
import { DebugService } from "src/app/services/debug.service";
import { ChatBotTextAnimationService } from "./textAnimationService.service";
import { PlayAudioHandlerService } from "./playAudioHandler.service";
import { ChatActionHandlerService } from "./chatActionsHandler.service";
import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { environment } from "src/environments/environment";
import { v4 as uuidv4 } from "uuid";
import { DataChatService } from "./dataChat.service";
import { RestService } from "src/app/services/rest.service";
// import { CustomRouteService } from 'src/app/pages/custom-route/services';
import { SessionStorageService } from 'src/app/services';

@Injectable({ providedIn: "root" })
export class WebSocketService {
  private ngZone = inject(NgZone);
  public http = inject(HttpClient);
  private rest = inject(RestService);
  private dataChat = inject(DataChatService);
  private playService = inject(PlayAudioHandlerService);
  private debugService = inject(DebugService);
  private actionService = inject(ChatActionHandlerService);
  private chatBotTextAnimationService = inject(ChatBotTextAnimationService);
  // private customRouteService = inject(CustomRouteService);
  private sessionStorageService = inject(SessionStorageService);

  private readonly urlWebSocket = computed(
    () =>
      `${environment.urlWebSocket}/${this.dataChat.token()}/${this.dataChat.id()}`,
  );
  private readonly urlWebSocketTools = computed(
    () => `${environment.urlWebSocketTools}/${this.dataChat.token()}`,
  );
  private readonly urlChatHistory = computed(
    () =>
      `${environment.urlChatHistory}?user_id=${this.dataChat.token()}&service=${this.dataChat.id()}`,
  );

  private wsSend!: WebSocketSubject<any>;
  private wsRecv$!: Observable<any>;
  private wsSub?: Subscription;

  private toolsSend!: WebSocketSubject<any>;
  private toolsRecv$!: Observable<any>;
  private toolsSub?: Subscription;

  private cancelled = new Set<string>();
  private cancel$ = new Subject<string>();
  private disconnect$ = new Subject<void>();
  // private audioFinishSubscription: Subscription;
  // private textFinishSubscription: Subscription;

  private connected = false;
  private actualUUID = null;

  private completionsIds = [];
  private completionType = new Map<string, "text" | "audio">();
  private completionFinal = new Map<string, any>();
  public historyChat = [];

  private feedbackOpenAIEffect = effect(
    () => {
      if (!this.actionService.openAIFeedback()) return;

      this.debugService.info(
        this.actionService.openAIFeedback(),
        "[WebSocketService] Sending Tools Feedback...",
      );
      this.toolsSend.next(this.actionService.openAIFeedback());
      this.actionService.openAIFeedback.set(null);
    },
    { allowSignalWrites: true },
  );

  connectAll() {
    if (this.connected) {
      this.debugService.warn(
        "Already Active WebSockets Connection",
        "[WebSocketService]",
      );
      return;
    }

    this.debugService.info("Connecting WebSocket...", "[WebSocketService]");
    const assitant = this.createSocket(this.urlWebSocket());
    this.wsSend = assitant.subject;
    this.wsRecv$ = assitant.recv$ as Observable<any>;

    const tools = this.createSocket(this.urlWebSocketTools());
    this.toolsSend = tools.subject;
    this.toolsRecv$ = tools.recv$ as Observable<any>;

    this.wsSub = this.setupAssistantPipeline()
      .pipe(takeUntil(this.disconnect$))
      .subscribe();

    this.toolsSub = this.setupToolsPipeline()
      .pipe(takeUntil(this.disconnect$))
      .subscribe();

    // this.audioFinishSubscription = this.playService.finished$.subscribe(() => {
    //   setTimeout(() => {
    //     this.ngZone.run(() => {
    //       console.log("DEBUGHERE: disableLoader audioFinishSubscription")
    //       // this.disableLoader();
    //     });
    //   }, 500);
    // });

    // this.textFinishSubscription =
    //   this.chatBotTextAnimationService.allIdle$.subscribe(() => {
    //     setTimeout(() => {
    //       this.ngZone.run(() => {
    //         console.log("DEBUGHERE: disableLoader chatBotTextAnimationService allIdle")
    //         // this.disableLoader();
    //       });
    //     }, 500);
    //   });

    this.connected = true;
  }

  private createSocket(url: string): {
    subject: WebSocketSubject<any>;
    recv$: Observable<any>;
  } {
    const subject = webSocket<any>({
      url,
      serializer: (v) => JSON.stringify(v),
      deserializer: (e) => {
        try {
          if (!e.data || e.data === "undefined") return null;
          return JSON.parse(e.data);
        } catch (err) {
          console.error("[WS] Error parsing JSON:", err, e.data);
          return null;
        }
      },
      openObserver: { next: () => this.debugService.info(url, "[WS] OPEN") },
      closeObserver: { next: () => this.debugService.info(url, "[WS] CLOSE") },
    });

    const recv$ = subject.pipe(
      retryWhen((errs) =>
        errs.pipe(
          scan((n) => n + 1, 0),
          takeUntil(this.disconnect$),
          concatMap((n) => {
            const base = Math.min(30_000, 1000 * 2 ** n);
            const jitter = Math.floor(Math.random() * 1000);
            return timer(base + jitter);
          }),
        ),
      ),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    return { subject, recv$ };
  }

  private setupAssistantPipeline(): Observable<void> {
    const completions$ = this.wsRecv$.pipe(
      tap((e) => {
        if (!this.completionsIds.includes(e.completion_id))
          this.debugService.warn(
            e.completion_id,
            "[WebSocketService] Not processing Assistant CompletionID",
          );
      }),
      filter(m => this.completionsIds.includes(m.completion_id) || this.completionsIds.includes(m.completion_id.split("_")?.[0])),
      groupBy(
        (m) => m.completion_id,
        (m) => m,
        (grp) =>
          merge(
            grp.pipe(
              filter((m) => m.event_type === "final"),
              take(1),
            ),
            grp.pipe(
              filter((m) => m.event_type === "error"),
              take(1),
            ),
            this.cancel$.pipe(filter((id) => id === grp.key)),
          ),
      ),
    );

    return completions$.pipe(
      mergeMap((group$) => this.handleOneCompletionGroup$(group$)),
    );
  }

  public trackCompletionId(id: string, type: "text" | "audio" = "text") {
    this.completionsIds = [...this.completionsIds, id];
    this.completionType.set(id, type);
    this.actualUUID = id;
  }

  private handleOneCompletionGroup$(group$: GroupedObservable<string, any>) {
    const id = group$.key!;

    const textChunksOnly = this.completionType.get(id) === "text";
    const shared$ = group$.pipe(
      shareReplay({ bufferSize: 128, refCount: true }),
    );

    //TODO: Check this
    const textChunks$ = shared$.pipe(
      filter((m) => m.event_type === "chunk" && !!m.content?.text_content),
      map((m) => ({
        text: m.content.text_content as string,
        hiddenCommand: m.content.hidden_command as string,
      })),
      shareReplay({ bufferSize: 256, refCount: true }),
    );

    const audioChunks$ = shared$.pipe(
      filter((m) => m.event_type === "chunk" && !!m.content?.audio_content),
      map((m) => m.content.audio_content),
      shareReplay({ bufferSize: 256, refCount: true }),
    );

    const transcript$ = shared$.pipe(
      filter((m) => m.event_type === "transcript"),
      take(1),
    );
    const final$ = shared$.pipe(
      filter((m) => m.event_type === "final" || m.event_type === "end"),
      take(1),
    );
    const error$ = shared$.pipe(
      filter((m) => m.event_type === "error"),
      take(1),
    );
    const cancelForId$ = this.cancel$.pipe(
      filter((x) => x === id),
      take(1),
    );
    const earlyStop$ = merge(error$, cancelForId$, this.disconnect$).pipe(
      take(1),
    );
    const stop$ = merge(final$, error$, cancelForId$, this.disconnect$).pipe(
      take(1),
    );

    // ---- PREBUFFER ---------------------------------------------
    const PRE_COUNT = 10;
    const PRE_MS = 20000;

    const textAccum$ = textChunks$.pipe(
      scan((acc: any[], x: any) => {
        acc.push(x);
        return acc;
      }, [] as any[]),
      startWith([]),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    const audioAccum$ = textChunksOnly
      ? of([] as any[])
      : audioChunks$.pipe(
          scan((acc: any[], x: any) => {
            acc.push(x);
            return acc;
          }, [] as any[]),
          startWith([]),
          shareReplay({ bufferSize: 1, refCount: true }),
        );

    const textTrigger$ = merge(
      textAccum$.pipe(
        filter((buf) => buf.length > 0),
        take(1),
      ),
      // textAccum$.pipe(filter(buf => buf.length >= PRE_COUNT)),
      // timer(PRE_MS),
      final$,
    ).pipe(take(1));

    const audioTrigger$ = textChunksOnly
      ? of(null)
      : merge(
          audioAccum$.pipe(filter((buf) => buf.length >= 1)), // Cambiar a 1 para no bloquear el texto
          // timer(PRE_MS),
          final$,
        ).pipe(take(1));

    const textPre$ = textTrigger$.pipe(
      withLatestFrom(textAccum$),
      map(([, buf]) => buf),
      // tap(chunks => console.log('[Prebuffer TEXT]', chunks)),
      takeUntil(earlyStop$),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    const audioPre$ = textChunksOnly
      ? of([] as any[])
      : audioTrigger$.pipe(
          withLatestFrom(audioAccum$),
          map(([, buf]) => buf),
          // tap(chunks => console.log('[Prebuffer AUDIO]', chunks)),
          takeUntil(earlyStop$),
          shareReplay({ bufferSize: 1, refCount: true }),
        );

    const start$ = combineLatest([textPre$, audioPre$]).pipe(
      take(1),
      tap(([textBuf, audioBuf]) => {
        const data = {
          textCount: textBuf.length,
          audioCount: audioBuf.length,
          textBuf,
          audioBuf,
        };

        this.debugService.info(data, "[WebSocketService] Prebuffer Ready");
      }),
      takeUntil(earlyStop$),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    const textStream$ = start$.pipe(
      mergeMap(([textPre]) =>
        concat(
          from(textPre).pipe(takeUntil(earlyStop$)),
          textChunks$.pipe(skip(textPre.length), takeUntil(stop$)),
        ),
      ),
    );

    const audioStream$ = start$.pipe(
      mergeMap(([_, audioPre]) =>
        concat(
          from(audioPre).pipe(takeUntil(earlyStop$)),
          audioChunks$.pipe(skip(audioPre.length), takeUntil(stop$)),
        ),
      ),
    );

    const textFlow$ = textStream$.pipe(
      tap((chunk) => {
        // chunk ahora es { text, hiddenCommand }
        const c = chunk as any;
        if (this.isCancelled(id)) return;

        this.disableLoader();
        this.chatBotTextAnimationService.enqueueText(
          id,
          c.text,
          textChunksOnly,
          c.hiddenCommand,
        );
      }),
    );

    const audioFlow$ = audioStream$.pipe(
      tap((chunk) => {
        if (this.isCancelled(id)) return;

        // this.playService.feedChunk(chunk);
      }),
    );

    const transcriptSideEffects$ = transcript$.pipe(
      tap((res) => {
        if (!this.isCancelled(id))
          this.chatBotTextAnimationService.createUserMessage(
            res?.content?.text_content,
          );
      }),
      ignoreElements(),
    );

    const finalSideEffects$ = final$.pipe(
      tap((res) => {
        this.completionFinal.set(res.completion_id, res);
        // this.playService.saveAudio(res);
        // this.playService.endSession();
        if (this.isCancelled(id)) {
          const finalContent = res?.content?.text_content;
          if (finalContent && finalContent !== "[DONE]") {
            this.chatBotTextAnimationService.handleAssistenAllMessage(
              res.completion_id,
              finalContent,
            );
          }
        }
      }),
      ignoreElements(),
    );

    const onError$ = error$.pipe(
      tap((err) => {
        this.showErrorBubble();
      }),
      ignoreElements(),
    );

    return merge(
      textFlow$,
      audioFlow$,
      finalSideEffects$,
      transcriptSideEffects$,
      onError$,
    );
  }

  private setupToolsPipeline() {
    return this.toolsRecv$.pipe(
      filter(e => e != null),
      tap((e) => {
        if (!this.completionsIds.includes(e.completion_id))
          this.debugService.warn(
            e.completion_id,
            "[WebSocketService] Not processing Assistant CompletionID",
          );
      }),
      filter(m => this.completionsIds.includes(m.completion_id) || this.completionsIds.includes(m.completion_id.split("_")?.[0])),
      concatMap((evt) =>
        this.processToolEvent(evt).pipe(
          rxTimeout({ each: 30000 }), // 30s
          catchError((err) => {
            this.debugService.error(
              err,
              "[WebSocketService] Action Tool Failed",
            );
            this.showErrorBubble();
            return EMPTY; // continúa con el siguiente evento
          }),
          concatMap(() => of(null).pipe(delay(500))), // espera 500ms antes del siguiente
        ),
      ),
      catchError(err => {
          this.debugService.error(err, '[WebSocketService] Tools pipeline crashed, restarting');
          return this.setupToolsPipeline();
      })
    );
  }

  private processToolEvent(evt: any): Observable<void> {
    const result = this.actionService.actionFromChatBot(evt) as unknown;
    return this.toObservable(result).pipe(
      tap((e) => {
        this.debugService.debug(evt, "[WebSocketService] Tool action done");
      }),
      mapTo(void 0),
    );
  }

  private toObservable<T>(
    value: T | Promise<T> | Observable<T>,
  ): Observable<T> {
    const maybeObs = value as any;
    if (maybeObs?.subscribe) return maybeObs as Observable<T>;

    if (value instanceof Promise) return from(value);

    return of(value as T);
  }

  public async cancelCompletion(writeRemaining = true) {
    this.disableLoader();
    await this.playService.stopNow();

    const id = this.actualUUID;
    if (!id || this.cancelled.has(id)) return;

    if (writeRemaining) {
      this.writeRemaining(id);
    }

    this.cancelled.add(id);
    this.cancel$.next(id);
    try {
      this.chatBotTextAnimationService.cancel(id, writeRemaining);
    } catch {}
  }

  private isCancelled(id: string) {
    return this.cancelled.has(id) || this.cancelled.has(id.split("_")?.[0]);
  }

  public async sendWSChatBotData(item, data = null) {
    // Al enviar un nuevo mensaje, cancelamos la respuesta anterior si estaba en curso
    await this.cancelCompletion(false); // false para dejar el texto por donde va

    this.dataChat.removeErrorMsgs();
    this.dataChat.hasHistory.set(true);
    this.enableLoader();

    //TODO: Change way to receive token?
    // const routeToken = this.customRouteService.token()
    //   ?? this.sessionStorageService.getItem<string>('customRouteToken')
    //   ?? undefined;

    const type = data === "tools" ? "tools" : "websocket";
    // const hasActiveRoute = (this.customRouteService.customRouteData()?.dailyRoutes?.length ?? 0) > 0;

    const payload = {
      completion_id: uuidv4(),
      type: "user_request",
      output_type: item.output_type ?? "text",
      content: item.content,
      subdomain: this.rest.subDomain,
      app: this.rest.subDomain,
      route_token: undefined,
      is_chunk: false,
      is_final: true,
      route_active: false
    };
    this.debugService.info(payload, "[WebSocketService] Sending Payload");
    // await this.playService.startSession();
    this.sendPayload(payload, type);
  }

  private sendPayload(payload: any, type: "tools" | "websocket") {
    this.actualUUID = payload.completion_id;
    try {
      this.wsSend.next(payload);
      this.completionsIds = [...this.completionsIds, payload.completion_id];
      this.completionType.set(payload.completion_id, payload.output_type);
    } catch (e) {
      this.showErrorBubble();
    }
  }

  private writeRemaining(id) {
    const fullText = this.completionFinal.get(id)?.content?.text_content;
    if (!fullText) return;

    this.chatBotTextAnimationService.handleAssistenAllMessage(id, fullText);
  }

  disconnect(): void {
    // 1) Cortar todos los streams dependientes (takeUntil, retryWhen, etc.)
    this.disconnect$.next();

    this.toolsSub?.unsubscribe();
    this.toolsSub = undefined;

    this.wsSub?.unsubscribe();
    this.wsSub = undefined;

    // this.audioFinishSubscription?.unsubscribe();
    // this.audioFinishSubscription = undefined;

    // this.textFinishSubscription?.unsubscribe();
    // this.textFinishSubscription = undefined;

    // 3) Cerrar websockets con cierre limpio (1000)
    try {
      this.wsSend?.complete();
    } catch {}
    try {
      this.toolsSend?.complete();
    } catch {}

    this.wsSend = undefined as any;
    this.toolsSend = undefined as any;

    // 4) Cancelar/limpiar estado local
    try {
      this.cancel$.complete();
    } catch {}
    this.cancelled.clear();

    // 5) (Opcional) si vas a reconectar luego:
    this.disconnect$.complete();
    this.cancel$ = new Subject<string>();
    this.disconnect$ = new Subject<void>();
    this.connected = false;
    this.debugService.warn("Websocket Disconnected", "[WebSocketService]");
  }

  public disableLoader() {
    this.dataChat.isStreaming.set(false);
    this.dataChat.chatIsTyping.set(false);
  }

  public enableLoader() {
    this.dataChat.isStreaming.set(true);
    this.dataChat.chatIsTyping.set(true);
  }

  private showErrorBubble() {
    this.chatBotTextAnimationService.showConnectionErrorMessage();
    this.disableLoader();
  }

  public getHistory() {
    return this.http.get<any>(this.urlChatHistory()).pipe(
      catchError((err: HttpErrorResponse) => {
        const serverStatus = err.error?.status;
        if (err.status === 400 && serverStatus === "error_processing") {
          console.warn("New Chat. Nothing to load...");
          return EMPTY;
        }

        return throwError(
          () => new Error("HTTP request getHistory failed", { cause: err }),
        );
      }),
    );
  }
}
